// ============================================================
// NSNOP Module — Router (index.tsx)
// Lazy-loads all sub-routes for the NSNOP module.
// Angular source: NsnopDashboardModule (nsnop-dashboard.module.ts)
// Note: The Angular wrapper (NsnopDashboardComponent) only contains
// a <router-outlet> so no sidebar wrapper is needed here.
// ============================================================
import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';

const NsnopHome = lazy(() => import('./home/NsnopHome'));
const CashTied = lazy(() => import('./cash-tied/CashTied'));
const CashUntied = lazy(() => import('./cash-untied/CashUntied'));
const CashFullUntied = lazy(() => import('./cash-full-untied/CashFullUntied'));
const MaterialTied = lazy(() => import('./material-tied/MaterialTied'));
const MaterialUntied = lazy(() => import('./material-untied/MaterialUntied'));
const VolunteersOld = lazy(() => import('./volunteers-old/VolunteersOld'));
const Volunteers = lazy(() => import('./volunteers/Volunteers'));
const ProjectList = lazy(() => import('./projectlist/ProjectList'));
const UserDetail = lazy(() => import('./user-detail/UserDetail'));
const DistrictwiseProject = lazy(
  () => import('./districtwise-project/DistrictwiseProject')
);
const EcProject = lazy(() => import('./ec-project/EcProject'));
const SchoolProject = lazy(() => import('./school-project/SchoolProject'));
const CancelledProject = lazy(
  () => import('./cancelled-project/CancelledProject')
);
const MaterialMaster = lazy(() => import('./material-master/MaterialMaster'));
const EcMeeting = lazy(() => import('./ec-meeting/EcMeeting'));
const PaymentList = lazy(() => import('./paymentlist/PaymentList'));
const AlumniList = lazy(() => import('./alumni-list/AlumniList'));
const SmcSnaTransaction = lazy(
  () => import('./smc-sna-transaction/SmcSnaTransaction')
);
const DlcSmcReport = lazy(() => import('./dlc-smc-report/DlcSmcReport'));
const ReachoutForm = lazy(() => import('./reachout-form/ReachoutForm'));
const WhatsappReport = lazy(() => import('./whatsapp-report/WhatsappReport'));
const WhatsappDonor = lazy(() => import('./whatsapp-donor/WhatsappDonor'));
const WhatsappEstimationReport = lazy(
  () => import('./whatsapp-estimation-report/WhatsappEstimationReport')
);
const Individual = lazy(() => import('./individual/Individual'));
const OtherDonation = lazy(() => import('./other-donation/OtherDonation'));
const PrePortalDonation = lazy(
  () => import('./pre-portal-donation/PrePortalDonation')
);
const ItemWiseBreakup = lazy(
  () => import('./item-wise-breakup/ItemWiseBreakup')
);
const ManualEntries = lazy(() => import('./manual-entries/ManualEntries'));
const AlumniDonationList = lazy(
  () => import('./alumni-donation-list/AlumniDonationList')
);
const AlumniDonationKind = lazy(
  () => import('./alumni-donation-kind/AlumniDonationKind')
);
const KindOldestCommitments = lazy(
  () => import('./kind-oldest-commitments/KindOldestCommitments')
);
const TiedManualEntries = lazy(
  () => import('./tied-manual-entries/TiedManualEntries')
);
const ExistingUserDonation = lazy(
  () => import('./existing-user-donation/ExistingUserDonation')
);
const ManualSchoolneedDonation = lazy(
  () => import('./manual-schoolneed-donation/ManualSchoolneedDonation')
);
const TiedCustomDonation = lazy(
  () => import('./tied-custom-donation/TiedCustomDonation')
);
const ManualSchoolneedEntries = lazy(
  () => import('./manual-schoolneed-entries/ManualSchoolneedEntries')
);

const NsnopModule: React.FC = () => {
  return (
    <Suspense fallback={<div className="text-center p-5">Loading...</div>}>
      <Routes>
        <Route path="home" element={<NsnopHome />} />
        <Route path="cash-tied" element={<CashTied />} />
        <Route path="cash-untied" element={<CashUntied />} />
        <Route path="cash-full-untied" element={<CashFullUntied />} />
        <Route path="material-tied" element={<MaterialTied />} />
        <Route path="material-untied" element={<MaterialUntied />} />
        <Route path="volunteers-old" element={<VolunteersOld />} />
        <Route path="volunteers" element={<Volunteers />} />
        <Route path="projectlist" element={<ProjectList />} />
        <Route path="user-detail" element={<UserDetail />} />
        <Route path="districtwise-project" element={<DistrictwiseProject />} />
        <Route path="ec-project" element={<EcProject />} />
        <Route path="school-project" element={<SchoolProject />} />
        <Route path="cancelled-project" element={<CancelledProject />} />
        <Route path="material-master" element={<MaterialMaster />} />
        <Route path="ec-meeting" element={<EcMeeting />} />
        <Route path="paymentlist" element={<PaymentList />} />
        <Route path="alumni-list" element={<AlumniList />} />
        <Route path="smc-sna-transaction" element={<SmcSnaTransaction />} />
        <Route path="dlc-smc-report" element={<DlcSmcReport />} />
        <Route path="reachout-form" element={<ReachoutForm />} />
        <Route path="whatsapp-report" element={<WhatsappReport />} />
        <Route path="whatsapp-donor" element={<WhatsappDonor />} />
        <Route
          path="whatsapp-estimation-report"
          element={<WhatsappEstimationReport />}
        />
        <Route path="individual" element={<Individual />} />
        <Route path="other-donation" element={<OtherDonation />} />
        <Route path="pre-portal-donation" element={<PrePortalDonation />} />
        <Route path="item-wise-breakup" element={<ItemWiseBreakup />} />
        <Route path="manual-entries" element={<ManualEntries />} />
        <Route path="alumni-donation-list" element={<AlumniDonationList />} />
        <Route path="alumni-donation-kind" element={<AlumniDonationKind />} />
        <Route
          path="kind-oldest-commitments"
          element={<KindOldestCommitments />}
        />
        <Route path="tied-manual-entries" element={<TiedManualEntries />} />
        <Route
          path="manual-schoolneed-entries"
          element={<ManualSchoolneedEntries />}
        />
        <Route
          path="manual-schoolneed-donation"
          element={<ManualSchoolneedDonation />}
        />
        <Route path="tied-custom-donation" element={<TiedCustomDonation />} />
        {/* Angular route name differs from the component folder name */}
        <Route
          path="existing-custom-donation-form"
          element={<ExistingUserDonation />}
        />
      </Routes>
    </Suspense>
  );
};

export default NsnopModule;
